import { type FeedbackRequest, newBlankFeedbackRequest } from '@ctw/shared/api/ai/types';
import type { FeedbackSubmissionPolarity } from '@ctw/shared/api/feedback/feedback-manager';
import { submitFeedbackSubmission } from '@ctw/shared/api/feedback/submit-feedback-submission';
import { Button } from '@ctw/shared/components/button';
import { ErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { notify, notifyFromDrawer } from '@ctw/shared/components/toast';
import { FeedbackComponent } from '@ctw/shared/content/chart-review/feedback/feedback.v2';
import type { usePatientSummaries } from '@ctw/shared/content/summarization/use-patient-summaries';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useDrawer } from '@ctw/shared/context/drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw } from '@ctw/shared/utils/tailwind';
import { pick } from 'lodash-es';
import { useCallback, useState } from 'react';

interface OpenPatientSummaryFeedbackDrawerArgs {
  initialFeedbackPolarity: FeedbackSubmissionPolarity;
}

interface UsePatientFeedbackDrawerProps {
  summaryResponse: ReturnType<typeof usePatientSummaries>;
}

export function usePatientSummaryFeedbackDrawer({
  summaryResponse,
}: UsePatientFeedbackDrawerProps) {
  const telemetry = useTelemetry();
  const { openDrawer, closeDrawer, isDrawerOpen } = useDrawer({
    boundaryName: 'PatientSummaryDrawer',
  });

  return {
    openPatientSummaryFeedbackDrawer: ({
      initialFeedbackPolarity,
    }: OpenPatientSummaryFeedbackDrawerArgs) => {
      openDrawer({
        telemetry,
        action: {
          title: 'Feedback',
          content: {
            body: (
              <PatientSummaryFeedbackDrawerContent
                summaryResponse={summaryResponse}
                initialFeedbackPolarity={initialFeedbackPolarity}
                closeDrawer={closeDrawer}
              />
            ),
          },
        },
      });
    },
    closePatientSummaryFeedbackDrawer: closeDrawer,
    isPatientSummaryFeedbackDrawerOpen: isDrawerOpen,
  };
}

interface PatientSummaryFeedbackDrawerContentProps {
  summaryResponse: UsePatientFeedbackDrawerProps['summaryResponse'];
  initialFeedbackPolarity: OpenPatientSummaryFeedbackDrawerArgs['initialFeedbackPolarity'];
  closeDrawer: ReturnType<typeof useDrawer>['closeDrawer'];
}

const PatientSummaryFeedbackDrawerContent = ({
  summaryResponse,
  initialFeedbackPolarity,
  closeDrawer,
}: PatientSummaryFeedbackDrawerContentProps) => {
  const { requestContext, ctwFetch } = useCTW();
  const telemetry = useTelemetry();
  const [feedbackRequest, setFeedbackRequest] = useState<FeedbackRequest>({
    ...newBlankFeedbackRequest({}),
    isThumbsUp: initialFeedbackPolarity === 'positive',
    // The checkbox renders checked by default (`contactPermission ?? true`), so seed the state to
    // match. Without this it stays `undefined` until toggled, and a user who leaves the box in its
    // apparently-checked default is recorded as having declined outreach.
    contactPermission: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitFeedback = useCallback(async () => {
    // The caller only invokes this once `canSubmitFeedback` (below) confirms `summaryJobId` is
    // set, but guard here too so `submitFeedback` stays correct independent of that call site.
    if (summaryResponse.summaryJobId === undefined) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await submitFeedbackSubmission({
        ctwFetch,
        env: requestContext.env,
        authToken: requestContext.authToken,
        attributes: {
          resourceType: 'PatientSummary',
          resourceId: summaryResponse.summaryJobId,
          polarity: feedbackRequest?.isThumbsUp === true ? 'positive' : 'negative',
          reasons: feedbackRequest?.reasons ?? [],
          notes: feedbackRequest?.content ?? '',
          optedInToOutreach: feedbackRequest?.contactPermission === true,
        },
      });

      // ctwFetch resolves rather than rejecting on a non-2xx, so failures are only visible here.
      if (!response.ok) {
        throw new Error(`Failed to submit patient summary feedback (${response.status})`);
      }

      closeDrawer();
      notify({ type: 'success', title: 'Feedback submitted' });
    } catch (error) {
      telemetry.trackError({
        message: 'Error submitting patient summary feedback',
        error: error instanceof Error ? error : new Error(String(error)),
      });
      notifyFromDrawer({ type: 'error', title: 'Unable to submit feedback. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  }, [requestContext, ctwFetch, summaryResponse, feedbackRequest, telemetry, closeDrawer]);

  const canSubmitFeedback =
    !isSubmitting &&
    feedbackRequest !== null &&
    feedbackRequest.isThumbsUp !== undefined &&
    summaryResponse.summaryJobId !== undefined;

  return (
    <ErrorBoundary
      boundaryName="Patient summary feedback drawer content"
      initialContext={{
        ...telemetry.getScopedContextProperties(),
        summaryResponseContext: pick(summaryResponse, [
          'summaryJobId',
          'lastRefreshed',
          'status',
          'error',
        ]),
      }}
    >
      {() => (
        <div className={tw`space-y-3 px-4 py-2`}>
          <FeedbackComponent feedback={feedbackRequest} onFeedbackUpdate={setFeedbackRequest} />
          <div className={tw`flex w-full items-center justify-between gap-5 px-4`}>
            <Button
              type="button"
              variant="link"
              className={tw`hover:underline`}
              onClick={closeDrawer}
            >
              Close
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                if (canSubmitFeedback) {
                  void submitFeedback();
                }
              }}
              disabled={!canSubmitFeedback}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
};
